import { memo, useMemo } from "react";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from "recharts";
import { DateAndValue } from "common/types/DateAndValue";
import { WeatherAPIData } from "common/weather/types/WeatherApiData";
import { safeApiDate } from "utils/DateTools";

type Props = {
  title: string;
  data: DateAndValue<WeatherAPIData>[];
  height?: number;
};

const WeatherChart = memo(
  (props: Props) => {
    const { title, data, height = 300 } = props;

    // データ変換をメモ化
    const chartData = useMemo(() => {
      return data.map((item) => ({
        date: item.date,
        rain: item.value?.rain,
        snow: item.value?.snow,
        maxTemperature: item.value?.maxTemperature,
        minTemperature: item.value?.minTemperature,
      }));
    }, [data]);

    const { minRain, maxRain, minTemp, maxTemp } = useMemo(() => {
      const minRain = chartData.reduce((prev, curr) => {
        return Math.min(prev, Math.min(curr.rain ?? 0, curr.snow ?? 0));
      }, 0);

      const maxRain = chartData.reduce((prev, curr) => {
        return Math.max(prev, Math.max(curr.rain ?? 0, curr.snow ?? 0));
      }, 100);

      const minTemp = chartData.reduce((prev, curr) => {
        return Math.min(prev, curr.minTemperature ?? 0);
      }, 0);

      const maxTemp = chartData.reduce((prev, curr) => {
        return Math.max(prev, curr.maxTemperature ?? 0);
      }, 50);

      return { minRain, maxRain, minTemp, maxTemp };
    }, [chartData]);

    return (
      <div className="mt-2 chart-border">
        <span className="h5 d-flex justify-content-center">{title}</span>

        <ResponsiveContainer width={"100%"} height={height}>
          <ComposedChart width={600} height={300} data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              tickLine={false}
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const date = safeApiDate(value);
                return `${(date.getMonth() + 1).toString()}/${date.getDate().toString()}`;
              }}
            />
            <YAxis
              yAxisId={1}
              tickCount={6}
              label={{ value: "気温", fontSize: 12, angle: -90 }}
              tick={{ fontSize: 12 }}
              domain={[minTemp, maxTemp]}
            />
            <YAxis
              yAxisId={2}
              tickCount={6}
              label={{ value: "降雨・降雪", fontSize: 12, angle: 90 }}
              tick={{ fontSize: 12 }}
              orientation="right"
              domain={[minRain, maxRain]}
            />
            <Tooltip
              labelFormatter={(value) => {
                const date = safeApiDate(value);
                return `${(date.getMonth() + 1).toString()}/${date.getDate().toString()}`;
              }}
            />
            <Legend />
            <Line
              yAxisId={1}
              type="monotone"
              dataKey="maxTemperature"
              name={`最高気温`}
              stroke="#f44"
              strokeWidth={2}
            />
            <Line
              yAxisId={1}
              type="monotone"
              dataKey="minTemperature"
              name={`最低気温`}
              stroke="#55f"
              strokeWidth={2}
            />
            <Bar yAxisId={2} dataKey="rain" name={"降雨"} fill="#00f8" stackId="precipitation" />
            <Bar yAxisId={2} dataKey="snow" name={"降雪"} fill="#48b8" stackId="precipitation" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // カスタム比較: dataの内容が同じならtrueを返して再レンダリングをスキップ
    return (
      prevProps.title === nextProps.title &&
      prevProps.height === nextProps.height &&
      prevProps.data.length === nextProps.data.length &&
      prevProps.data === nextProps.data // 参照比較（親でメモ化されている場合）
    );
  }
);

WeatherChart.displayName = "WeatherChart";

export { WeatherChart };
